import React, { useState } from 'react';
import { 
  Card, 
  Alert, 
  Button, 
  Form, 
  ProgressBar
} from 'react-bootstrap';

const PracticeQuestionSession = ({ questions, onEndSession, selectedModel, apiBaseUrl }) => {
  const [questionStates, setQuestionStates] = useState(
    questions.map(q => ({
      question: q,
      userAnswer: '',
      submitted: false,
      feedback: null
    }))
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [sessionScore, setSessionScore] = useState(0);

  const currentQuestionState = questionStates[currentQuestionIndex];
  const currentQuestion = currentQuestionState.question;

  // Find unanswered questions
  const unansweredQuestions = questionStates.reduce((acc, state, index) => {
    if (!state.submitted) acc.push(index);
    return acc;
  }, []);

  const handleAnswerChange = (e) => {
    const newQuestionStates = [...questionStates];
    newQuestionStates[currentQuestionIndex] = {
      ...newQuestionStates[currentQuestionIndex],
      userAnswer: e.target.value
    };
    setQuestionStates(newQuestionStates);
  };

  const handleSubmitAnswer = async () => {
    const currentState = questionStates[currentQuestionIndex];
    
    if (!currentState.userAnswer.trim()) {
      setError('Please enter an answer before submitting');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('user_answer', currentState.userAnswer);
      formData.append('correct_answer', currentQuestion.answer);
      formData.append('question', currentQuestion.question);
      formData.append('ai_provider', selectedModel);

      const response = await fetch(`${apiBaseUrl}/check-answer`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to submit answer');
      }

      const data = await response.json();
      
      const newQuestionStates = [...questionStates];
      newQuestionStates[currentQuestionIndex] = {
        ...currentState,
        submitted: true,
        feedback: data
      };
      setQuestionStates(newQuestionStates);

    } catch (err) {
      console.error('Error submitting answer:', err);
      setError('Failed to check answer. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNextQuestion = () => {
    // Find next unanswered question
    const nextIndex = unansweredQuestions.find(i => i > currentQuestionIndex);
    if (nextIndex !== undefined) {
      setCurrentQuestionIndex(nextIndex);
    } else if (unansweredQuestions.length === 0) {
      // All questions answered, show summary
      calculateSessionScore();
      setShowSummaryModal(true);
    }
  };

  const handlePreviousQuestion = () => {
    // Find previous unanswered question
    const prevIndex = [...unansweredQuestions]
      .reverse()
      .find(i => i < currentQuestionIndex);
    
    if (prevIndex !== undefined) {
      setCurrentQuestionIndex(prevIndex);
    }
  };

  const calculateSessionScore = () => {
    const submittedQuestions = questionStates.filter(state => state.submitted);
    const totalScore = submittedQuestions.reduce((sum, state) => 
      sum + (state.feedback?.accuracy || 0), 0);
          const avgScore = submittedQuestions.length > 0 
      ? Math.round(totalScore / submittedQuestions.length)
      : 0;
    setSessionScore(avgScore);
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return 'success';
    if (accuracy >= 60) return 'info';
    if (accuracy >= 40) return 'warning';
    return 'danger';
  };

  return (
    <div>
      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5>Question {currentQuestionIndex + 1} of {questions.length}</h5>
              <div className="text-muted small">Topic: {currentQuestion.topic}</div>
              <div className="text-muted small">Difficulty: {currentQuestion.difficulty}</div>
            </div>
            <Button variant="outline-secondary" onClick={onEndSession}>
              End Session
            </Button>
          </div>
          
          <div className="d-flex justify-content-between mb-3">
            <Button 
              variant="outline-primary" 
              onClick={handlePreviousQuestion}
              disabled={unansweredQuestions[0] === currentQuestionIndex}
            >
              Previous Unanswered
            </Button>
            <Button 
              variant="outline-primary" 
              onClick={handleNextQuestion}
              disabled={unansweredQuestions.length === 0}
            >
              Next Unanswered
            </Button>
          </div>
          
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Card className="bg-light mb-3">
            <Card.Body>
              <Card.Title>Question</Card.Title>
              <Card.Text>{currentQuestion.question}</Card.Text>
            </Card.Body>
          </Card>
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Your Answer</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={currentQuestionState.userAnswer}
                onChange={handleAnswerChange}
                disabled={currentQuestionState.submitted}
                placeholder="Type your answer here..."
              />
            </Form.Group>
            
            {!currentQuestionState.submitted ? (
              <Button 
                variant="primary" 
                onClick={handleSubmitAnswer}
                disabled={loading || !currentQuestionState.userAnswer.trim()}
              >
                {loading ? 'Checking...' : 'Submit Answer'}
              </Button>
            ) : null}
          </Form>
          
          {currentQuestionState.submitted && currentQuestionState.feedback && (
            <Card className="mt-4">
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Feedback</h5>
                  <div>
                    <ProgressBar 
                      now={currentQuestionState.feedback.accuracy} 
                      label={`${Math.round(currentQuestionState.feedback.accuracy)}%`} 
                      variant={getAccuracyColor(currentQuestionState.feedback.accuracy)}
                      style={{ width: '120px', height: '24px' }}
                    />
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                <p>{currentQuestionState.feedback.feedback}</p>
                
                {currentQuestionState.feedback.missing_points && currentQuestionState.feedback.missing_points.length > 0 && (
                  <div className="mb-3">
                    <h6>Missing Points:</h6>
                    <ul className="text-danger">
                      {currentQuestionState.feedback.missing_points.map((point, index) => (
                        <li key={index}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {currentQuestionState.feedback.correct_points && currentQuestionState.feedback.correct_points.length > 0 && (
                  <div>
                    <h6>Correct Points:</h6>
                    <ul className="text-success">
                      {currentQuestionState.feedback.correct_points.map((point, index) => (
                        <li key={index}>{point}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="mt-4">
                  <h6>Correct Answer:</h6>
                  <div className="p-3 bg-light rounded">
                    {currentQuestion.answer}
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}
        </Card.Body>
      </Card>
      
      {/* Session Summary Modal */}
      {showSummaryModal && (
        <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Practice Session Summary</h5>
                <Button variant="secondary" onClick={onEndSession}>Close</Button>
              </div>
              <div className="modal-body">
                <div className="text-center mb-4">
                  <h3>Your Overall Score</h3>
                  <div className="d-flex justify-content-center">
                    <ProgressBar 
                      now={sessionScore} 
                      label={`${sessionScore}%`}
                      variant={getAccuracyColor(sessionScore)}
                      style={{ height: '30px', width: '50%' }}
                    />
                  </div>
                </div>
                
                <h5>Question Performance</h5>
                <div className="list-group mb-4">
                  {questionStates.filter(state => state.submitted).map((state, index) => (
                    <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                      <div>
                        <strong>Q{index + 1}:</strong> {state.question.question.substring(0, 100)}...
                      </div>
                      <ProgressBar 
                        now={state.feedback?.accuracy || 0} 
                        label={`${Math.round(state.feedback?.accuracy || 0)}%`}
                        variant={getAccuracyColor(state.feedback?.accuracy || 0)}
                        style={{ width: '100px' }}
                      />
                    </div>
                  ))}
                </div>
                
                <div className="text-center">
                  <p>
                    {sessionScore >= 80 ? 'Excellent work! You have a strong understanding of this material.' :
                     sessionScore >= 60 ? 'Good job! With a bit more study, you\'ll master this material.' :
                     sessionScore >= 40 ? 'Keep practicing! Review the areas where you scored lower.' :
                     'More study is needed. Focus on understanding the core concepts.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PracticeQuestionSession;